import { toEntries } from "~/tools/to-entries";
import { toValues } from "~/tools/to-values";

import type { Permission, SubjectPermissionsResponse } from "../_dto";

export type ResourcePermission = Extract<
	Permission,
	// *Views
	| Permission.GetViews
	| Permission.UpdateViews
	| Permission.DeleteViews
	// *Dashboards
	| Permission.GetDashboards
	| Permission.UpdateDashboards
	| Permission.CreateDashboards
	| Permission.DeleteDashboards
	// *Topology
	| Permission.GetTopology
>;

export type SystemPermission = Permission;

/**
 * Represents a collection of system and resource-level permissions.
 *
 * This class provides methods to check if specific permissions exist
 * at the system level or for specific resources.
 *
 * @example
 * ```typescript
 * const permissions = Permissions.init({
 *   systemPermissions: ['read', 'write'],
 *   resourcePermissions: { 'resource1': ['read'], 'resource2': ['write'] }
 * });
 *
 * permissions.hasSystemPermission('read'); // true
 * permissions.hasResourcePermission('resource1', 'write'); // false
 * permissions.hasResourcePermission('resource2', 'write'); // true
 * permissions.hasResourcePermission('write'); // true (exists for any resource)
 * ```
 */
export class Permissions {
	public static init({
		resourcePermissions,
		systemPermissions,
	}: Pick<SubjectPermissionsResponse, "resourcePermissions" | "systemPermissions">): Permissions {
		return new Permissions(
			new Set(systemPermissions),
			new Map(toEntries(resourcePermissions).map(([key, values]) => [key, new Set(values)])),
			new Set(toValues(resourcePermissions).flat()),
		);
	}

	private constructor(
		private readonly system: ReadonlySet<Permission>,
		private readonly resource: ReadonlyMap<string, ReadonlySet<Permission>>,
		private readonly allResource: ReadonlySet<Permission>,
	) {}

	/**
	 * Checks if the user has a specific system-level permission.
	 *
	 * @param permission - The permission to check for in the system permissions set.
	 * @returns `true` if the user has the specified system permission, `false` otherwise.
	 */
	public hasSystemPermission(permission: SystemPermission): boolean {
		return this.system.has(permission);
	}

	/**
	 * Checks if the permission exists for any resource.
	 * First it checks for system-level permission, if not found it checks for the permission across all resources.
	 *
	 * @param permission - The permission to check for across all resources
	 * @returns true if any resource has the specified permission, false otherwise
	 */
	public hasResourcePermission(permission: ResourcePermission): boolean;

	/**
	 * Checks if a permission exists for a specific resource.
	 * First it checks for system-level permission, if not found it checks for the permission on the specified resource.
	 *
	 * @param resource - The specific resource identifier to check permissions for
	 * @param permission - The permission to check for on the specified resource
	 * @returns true if the specified resource has the permission, false otherwise
	 */
	public hasResourcePermission(resource: string, permission: ResourcePermission): boolean;

	public hasResourcePermission(...args: [ResourcePermission] | [string, ResourcePermission]): boolean {
		const [resource, permission] = args.length === 1 ? [null, args[0]] : args;

		if (this.hasSystemPermission(permission)) {
			return true;
		}

		if (resource == null) {
			return this.allResource.has(permission);
		}

		return this.resource.get(resource)?.has(permission) ?? false;
	}
}
